'use client';

import { useEffect, useRef } from 'react';

import { motion, useAnimation, useInView } from 'framer-motion';

import BoxReveal from '@/components/ui/box-reveal';
import { TextAnimate } from '@/components/ui/text-animate';
import { ny } from '@/lib/utils';

type TAnimatedInfoLineProps = {
    title?: string;
    firstTitle?: string;
    number?: string;
    text?: string;

    classnames?: {
        root?: string;
        info?: string;
        number?: string;
        title?: string;
        firstTitle?: string;
        text?: string;
        underline?: string;
    };
};

export function AnimatedTexnoLine({
    text = '',
    title = '',
    firstTitle = '',
    classnames = {},
    number,
}: TAnimatedInfoLineProps) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-50px 0px' }); // Триггер, когда компонент попадает в зону видимости

    const underlineControls = useAnimation();
    const textControls = useAnimation();

    useEffect(() => {
        if (isInView) {
            async function runAnimation() {
                await underlineControls.start({
                    scaleX: 1,
                    transition: { delay: 0.2, duration: 0.5, ease: 'easeOut' },
                });
                await textControls.start({
                    filter: 'blur(0px)',
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.3, duration: 0.8 },
                });
            }
            runAnimation();
        }
    }, [isInView, underlineControls, textControls]);

    return (
        <div ref={ref} className={ny('info-list jstify-center flex flex-col gap-3', classnames.root)}>
            <div className={ny('info-list--row relative flex flex-col p-4', classnames.info)}>
                <div className={'relative flex flex-wrap items-center gap-x-2'}>
                    <TextAnimate
                        className={ny(
                            'absolute -top-[10px] left-[-22px] text-3xl font-light opacity-70 lg:left-[-3%] xl:top-[2rem]',
                            classnames.number
                        )}
                        once={true}
                        startOnView={false}
                        animation="slideLeft"
                        delay={0.2}
                        by="line"
                        as="span"
                    >
                        {`${number}`}
                    </TextAnimate>
                    {firstTitle ? (
                        <BoxReveal boxColor={'#83b1df'} duration={0.7} width="fit-content">
                            <span className={ny('whitespace-nowrap leading-none text-primary', classnames.firstTitle)}>
                                {firstTitle}
                            </span>
                        </BoxReveal>
                    ) : null}

                    <TextAnimate
                        className={ny('text-primary-foreground', classnames.title)}
                        once={true}
                        startOnView={false}
                        animation="slideRight"
                        delay={0.5}
                        by="line"
                        as="span"
                    >
                        {title}
                    </TextAnimate>
                </div>
                <motion.p
                    className={ny('mb-10 flex w-[90%] text-primary-foreground md:w-[80%] lg:w-[50%]', classnames.text)}
                    style={{ lineHeight: '1.75rem' }}
                    initial={{ filter: 'blur(10px)', opacity: 0, y: 10 }}
                    animate={textControls}
                >
                    {text}
                </motion.p>

                <motion.div
                    className={ny(
                        'info-list--row-underline absolute bottom-0 left-0 right-0 h-1 bg-primary-foreground',
                        classnames.underline
                    )}
                    initial={{ scaleX: 0 }}
                    animate={underlineControls}
                    style={{ originX: 0 }}
                />
            </div>
        </div>
    );
}
